import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiService from '../../services/apiService';

const CommonSettings = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('Add Common Setting');
  const [formData, setFormData] = useState({ id: '', type: '', value: '' });
  const [deleteId, setDeleteId] = useState('');
  const recordsPerPage = 10;

  useEffect(() => {
    fetchItems();
  }, [currentPage, search]);

  const fetchItems = async () => {
    try {
      const response = await apiService.post('getCommon', {
        start: (currentPage - 1) * recordsPerPage,
        length: recordsPerPage,
        search
      });

      if (response.data) {
        const total = response.recordsFiltered || response.data.length || 0;
        setItems(response.data);
        setTotalRecords(total);
        setTotalPages(Math.ceil(total / recordsPerPage));
      }
    } catch (error) {
      console.error('Error fetching common settings:', error);
      toast.error('Failed to load common settings');
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i += 1) {
      pages.push(i);
    }

    return pages;
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const openModal = () => {
    setModalTitle('Add Common Setting');
    setFormData({ id: '', type: '', value: '' });
    setShowModal(true);
  };

  const editItem = async (id) => {
    try {
      const response = await apiService.get(`editCommon/${id}`);
      if (response.status) {
        const item = response.data.data;
        setModalTitle('Edit Common Setting');
        setFormData({
          id: item._id,
          type: item.type || '',
          value: item.value || ''
        });
        setShowModal(true);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error(error);
      toast.error('Something went wrong!');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await apiService.post('updateCommon', formData);
      if (response.status) {
        toast.success(response.message);
        setShowModal(false);
        fetchItems();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error(error);
      toast.error('Something went wrong!');
    }
  };

  const confirmDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const deleteItem = async () => {
    try {
      const response = await apiService.get(`deleteCommon/${deleteId}`);
      if (response.status) {
        toast.success(response.message);
        setShowDeleteModal(false);
        fetchItems();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error(error);
      toast.error('Something went wrong!');
    }
  };

  const toggleStatus = async (id) => {
    try {
      const response = await apiService.get(`commonStatus/${id}`);
      if (response.status) {
        toast.success(response.message);
        fetchItems();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error(error);
      toast.error('Something went wrong!');
    }
  };

  return (
    <div className="flex-1 overflow-auto bg-gray-50">
      <div className="bg-white rounded-lg shadow-sm m--">
        <div className="flex items-center justify-between p-3 border-b">
          <div>
            <nav className="flex text-sm text-gray-600 mb-2">
              <span className="cursor-pointer hover:text-[#009ef7]" onClick={() => navigate('/dashboard')}>Home</span>
              <span className="mx-2">/</span>
              <span className="text-gray-900">Common Settings</span>
            </nav>
          </div>
          <button
            onClick={openModal}
            className="px-2 sm:px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90"
            style={{ backgroundColor: '#009ef7' }}
          >
            <i className="bi bi-plus text-sm mr-1"></i>
            Add New
          </button>
        </div>

        <div className="p-6">
          <div className="flex gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <i className="bi bi-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                  placeholder="Search by type or value"
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                />
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs sm:text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-600 text-sm">
                  <th className="px-4 py-3 text-left font-semibold">#</th>
                  <th className="px-4 py-3 text-left font-semibold min-w-[220px]">Type</th>
                  <th className="px-4 py-3 text-left font-semibold min-w-[260px]">Value</th>
                  <th className="px-4 py-3 text-left font-semibold">Status</th>
                  <th className="px-4 py-3 text-left font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-gray-500">No items found</td>
                  </tr>
                ) : (
                  items.map((item, index) => (
                    <tr key={item._id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-900">{(currentPage - 1) * recordsPerPage + index + 1}</td>
                      <td className="px-4 py-3 text-gray-900">{item.type || '--'}</td>
                      <td className="px-4 py-3 text-gray-900 break-words">{item.value || '--'}</td>
                      <td className="px-4 py-3">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={item.status === 1}
                            onChange={() => toggleStatus(item._id)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#009ef7]"></div>
                        </label>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => editItem(item._id)}
                            className="px-3 py-1 text-xs font-semibold rounded"
                            style={{ backgroundColor: '#d4f4dd', color: '#1bc943' }}
                          >
                            <i className="bi bi-pencil text-sm"></i>
                          </button>
                          <button
                            onClick={() => confirmDelete(item._id)}
                            className="px-3 py-1 text-xs font-semibold rounded"
                            style={{ backgroundColor: '#ffe2e5', color: '#f1416c' }}
                          >
                            <i className="bi bi-trash text-sm"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalRecords > 0 && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-gray-600">
                Showing {(currentPage - 1) * recordsPerPage + 1} to {Math.min(currentPage * recordsPerPage, totalRecords)} of {totalRecords} results
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {getPageNumbers().map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-1 text-sm border rounded ${
                      currentPage === page ? 'text-white' : 'border-gray-300 hover:bg-gray-50'
                    }`}
                    style={currentPage === page ? { backgroundColor: '#009ef7', borderColor: '#009ef7' } : {}}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">{modalTitle}</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="bi bi-x text-2xl"></i>
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    placeholder="Enter type"
                    required
                    className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Value <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.value}
                    onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                    placeholder="Enter value"
                    required
                    rows="4"
                    className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                  />
                </div>
              </div>
              <div className="flex flex-col sm:flex-row justify-end gap-3 p-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90"
                  style={{ backgroundColor: '#009ef7' }}
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Confirmation</h3>
              <button onClick={() => setShowDeleteModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="bi bi-x text-2xl"></i>
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-700">Are you sure you want to delete this item?</p>
            </div>
            <div className="flex flex-col sm:flex-row justify-end gap-3 p-4 border-t">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={deleteItem}
                className="px-6 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommonSettings;
